// src/features/workflows/RunDetailPage.jsx
//
// TASK-4584: Run Detail — header, tabs (Overview/Phases/Tasks/Details/
// Activity/Files), progress bar, stat cards, phase list, need-attention
// panel, participants, activity feed. editor/admin can advance the run's
// current phase; viewers are read-only.
//
// Tasks/Details/Files tabs are stubs per task scope ("full Tasks/Details/
// Files tab functionality" is out of scope for V1) — Overview, Phases, and
// Activity are fully implemented.

import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft, ArrowUpRight, Workflow, CheckCircle2, Circle, Lock, AlertTriangle,
  Calendar, User, Users, Loader2, ChevronDown,
} from "lucide-react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import {
  getWorkflowRun, advanceWorkflowRunPhase, updateWorkflowRunStatus,
  assignWorkflowRunPhaseOwner, assignWorkflowRunSubstep, completeWorkflowRunSubstep,
  decideWorkflowRunPhaseApproval, decideWorkflowRunSubstepApproval,
  resubmitWorkflowRunPhaseApproval, resubmitWorkflowRunSubstepApproval,
  getWorkflowAssignableUsers,
} from "../../data/workflowsClient.js";
import { log } from "../../utils/logger.js";

const TABS = ["Overview", "Phases", "Tasks", "Details", "Activity", "Files"];

const STATUS_META = {
  in_progress: { label: "In progress", bg: T.blueSoft, fg: T.blue },
  on_hold:     { label: "On hold",     bg: T.negotiateBg ?? T.bg, fg: T.negotiateOrange ?? T.mid },
  completed:   { label: "Completed",   bg: T.greenSoft, fg: T.green },
};

const STATUS_OPTIONS = ["in_progress", "on_hold", "completed"];

const SLA_META = {
  on_track: { label: "On track", bg: T.greenSoft, fg: T.green },
  at_risk:  { label: "At risk",  bg: T.negotiateBg ?? T.bg, fg: T.negotiateOrange ?? T.mid },
  overdue:  { label: "Overdue",  bg: T.redSoft, fg: T.red },
};

function SlaBadge({ state, testId }) {
  const meta = SLA_META[state];
  if (!meta) return null;
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", fontSize: T.fsXxs, fontWeight: T.fwBold,
        color: meta.fg, background: meta.bg, borderRadius: 5, padding: "2px 7px",
      }}
    >
      {meta.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  const s = typeof d === "string" ? d : String(d);
  const datePart = s.includes("T") ? s.slice(0, s.indexOf("T")) : s;
  const parts = datePart.split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return datePart;
  const [y, m, day] = parts;
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{ flex: 1, minWidth: 130, background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: T.fsXxs, color: T.mid, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: T.fsLg, fontWeight: T.fwBold, color: T.dark }}>{value}</div>
      {sub && <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function RunDetailPage({ runId, canEdit, currentRole = "viewer", currentUserId = null, onBack, onNavigateToSource }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [advancing, setAdvancing] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusEditing, setStatusEditing] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assigning, setAssigning] = useState(null); // id currently being reassigned
  const [completing, setCompleting] = useState(null); // substep id currently toggling
  const [decidingApproval, setDecidingApproval] = useState(null); // approval id currently being decided

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await getWorkflowRun(runId);
      setRun(detail);
    } catch (err) {
      log.error("RunDetailPage: failed to load run", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canEdit) return;
    getWorkflowAssignableUsers()
      .then((list) => setAssignableUsers(list))
      .catch((err) => log.error("RunDetailPage: failed to load assignable users", err));
  }, [canEdit]);

  async function handleAssignPhaseOwner(phaseId, userId) {
    setAssigning(phaseId);
    setError(null);
    try {
      await assignWorkflowRunPhaseOwner(runId, phaseId, userId);
      await load();
    } catch (err) {
      log.error("RunDetailPage: failed to reassign phase owner", err);
      setError(err);
    } finally {
      setAssigning(null);
    }
  }

  async function handleAssignSubstep(substepId, userId) {
    setAssigning(substepId);
    setError(null);
    try {
      await assignWorkflowRunSubstep(runId, substepId, userId);
      await load();
    } catch (err) {
      log.error("RunDetailPage: failed to reassign sub-step", err);
      setError(err);
    } finally {
      setAssigning(null);
    }
  }

  async function handleToggleSubstep(substepId, done) {
    setCompleting(substepId);
    setError(null);
    try {
      await completeWorkflowRunSubstep(runId, substepId, done);
      await load();
    } catch (err) {
      log.error("RunDetailPage: failed to toggle sub-step", err);
      setError(err);
    } finally {
      setCompleting(null);
    }
  }

  async function handleDecidePhaseApproval(phaseId, decision, note) {
    setDecidingApproval(phaseId);
    setError(null);
    try {
      await decideWorkflowRunPhaseApproval(runId, phaseId, decision, note);
      await load();
    } catch (err) {
      log.error("RunDetailPage: failed to decide phase approval", err);
      setError(err);
    } finally {
      setDecidingApproval(null);
    }
  }

  async function handleDecideSubstepApproval(substepId, decision, note) {
    setDecidingApproval(substepId);
    setError(null);
    try {
      await decideWorkflowRunSubstepApproval(runId, substepId, decision, note);
      await load();
    } catch (err) {
      log.error("RunDetailPage: failed to decide sub-step approval", err);
      setError(err);
    } finally {
      setDecidingApproval(null);
    }
  }

  async function handleResubmitPhaseApproval(phaseId) {
    setDecidingApproval(phaseId);
    setError(null);
    try {
      await resubmitWorkflowRunPhaseApproval(runId, phaseId);
      await load();
    } catch (err) {
      log.error("RunDetailPage: failed to resubmit phase approval", err);
      setError(err);
    } finally {
      setDecidingApproval(null);
    }
  }

  async function handleResubmitSubstepApproval(substepId) {
    setDecidingApproval(substepId);
    setError(null);
    try {
      await resubmitWorkflowRunSubstepApproval(runId, substepId);
      await load();
    } catch (err) {
      log.error("RunDetailPage: failed to resubmit sub-step approval", err);
      setError(err);
    } finally {
      setDecidingApproval(null);
    }
  }

  async function handleAdvance(phaseId) {
    setAdvancing(true);
    setError(null);
    try {
      await advanceWorkflowRunPhase(runId, phaseId);
      await load();
    } catch (err) {
      log.error("RunDetailPage: failed to advance phase", err);
      setError(err);
    } finally {
      setAdvancing(false);
    }
  }

  async function handleStatusChange(newStatus) {
    if (!newStatus || newStatus === run?.status) { setStatusEditing(false); return; }
    setStatusUpdating(true);
    setError(null);
    try {
      await updateWorkflowRunStatus(runId, newStatus);
      await load();
      setStatusEditing(false);
    } catch (err) {
      log.error("RunDetailPage: failed to update status", err);
      setError(err);
    } finally {
      setStatusUpdating(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 16, fontSize: T.fsSm, color: T.mid }}>Loading run…</div>;
  }
  if (error && !run) {
    return <ErrorBanner error={error} onDismiss={() => setError(null)} />;
  }
  if (!run) return null;

  const phases = run.phases || [];
  // Progress (and run completion, mirrored server-side in maybeCompleteRun)
  // is gated ONLY by phases flagged `required` — optional phases must never
  // hold back the bar. Zero required phases means nothing to wait on, so
  // it renders as 100% / complete-eligible rather than falling back to
  // counting optional phases.
  const requiredPhases = phases.filter((p) => p.required);
  const totalPhases = requiredPhases.length;
  const donePhases = requiredPhases.filter((p) => p.status === "done").length;
  const progressPct = totalPhases > 0 ? Math.round((donePhases / totalPhases) * 100) : 100;
  const currentPhase = phases.find((p) => p.status === "current");
  const statusMeta = STATUS_META[run.status] || STATUS_META.in_progress;
  const lockedNeedingAttention = phases.filter((p) => p.status === "locked" && p.lockReason);
  const allSubsteps = phases.flatMap((p) => (p.substeps || []).map((s) => ({ ...s, phaseStatus: p.status, phaseName: p.name })));
  const myOpenSubsteps = allSubsteps.filter(
    (s) => s.status !== "done" && s.phaseStatus === "current" && currentUserId && s.assigneeUserId === currentUserId
  );

  return (
    <div data-testid="run-detail-page">
      <button
        type="button"
        data-testid="run-detail-back"
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: T.mid, fontSize: T.fsXs, cursor: "pointer", marginBottom: 12, padding: 0 }}
      >
        <ArrowLeft size={14} /> Back to Active Runs
      </button>

      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Workflow size={16} color={T.mid} />
              <span style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark }}>{run.name}</span>
              {statusEditing && canEdit ? (
                <select
                  data-testid="run-detail-status-select"
                  autoFocus
                  disabled={statusUpdating}
                  value={run.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  onBlur={() => setStatusEditing(false)}
                  style={{
                    fontSize: T.fsXxs, fontWeight: T.fwBold, borderRadius: 5, padding: "3px 8px",
                    border: `1px solid ${T.border}`, background: T.white, color: T.dark, cursor: "pointer",
                  }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{STATUS_META[s].label}</option>
                  ))}
                </select>
              ) : (
                <span
                  style={{
                    background: statusMeta.bg, color: statusMeta.fg, fontSize: T.fsXxs, fontWeight: T.fwBold,
                    textTransform: "uppercase", letterSpacing: 0.3, borderRadius: 5, padding: "3px 8px",
                  }}
                >
                  {statusMeta.label}
                </span>
              )}
              {canEdit && !statusEditing && (
                <button
                  type="button"
                  data-testid="run-detail-update-status"
                  onClick={() => setStatusEditing(true)}
                  disabled={statusUpdating}
                  style={{
                    background: "transparent", border: `1px solid ${T.border}`, borderRadius: 6,
                    padding: "3px 8px", fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.mid,
                    cursor: statusUpdating ? "default" : "pointer", display: "flex", alignItems: "center", gap: 4,
                  }}
                >
                  {statusUpdating && <Loader2 size={11} />} Update status
                </button>
              )}
            </div>
            <div style={{ fontSize: T.fsXs, color: T.mid }}>{run.templateName}</div>
            {run.sourceType && run.sourceId && (
              <button
                type="button"
                data-testid="run-detail-source-link"
                onClick={() => onNavigateToSource?.(run.sourceType, run.sourceId, run.locationId)}
                disabled={!onNavigateToSource}
                style={{
                  display: "flex", alignItems: "center", gap: 4, marginTop: 4,
                  background: "transparent", border: "none", padding: 0,
                  fontSize: T.fsXs, color: T.blue, cursor: onNavigateToSource ? "pointer" : "default",
                }}
              >
                Started from {run.sourceLabel || run.sourceType} <ArrowUpRight size={12} />
              </button>
            )}
          </div>
          <div data-testid="run-detail-progress" style={{ textAlign: "right", minWidth: 180 }}>
            <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 4 }}>
              {progressPct}% · {donePhases} of {totalPhases} required phases
            </div>
            <div style={{ width: 180, height: 7, borderRadius: 4, background: T.bg, overflow: "hidden" }}>
              <div style={{ width: `${progressPct}%`, height: "100%", background: T.blue, borderRadius: 4 }} />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <StatCard label="Owner" value={run.ownerName || "Unassigned"} />
          <StatCard label="Requestor" value={run.requestorName || "—"} />
          <StatCard label="Started" value={fmtDate(run.startedAt || run.createdAt)} />
          <StatCard
            label="Target date"
            value={fmtDate(run.targetCompletion || run.dueDate)}
            sub={run.slaState && SLA_META[run.slaState] ? <SlaBadge state={run.slaState} testId="run-detail-sla-badge" /> : null}
          />
        </div>
      </div>

      <div role="tablist" aria-label="Run detail tabs" style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 16 }}>
        {TABS.map((t) => {
          const active = t === tab;
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`run-detail-tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              style={{
                border: "none", background: "transparent", cursor: "pointer",
                padding: "8px 14px", fontSize: T.fsSm5, fontWeight: active ? T.fwBold : T.fwRegular,
                color: active ? T.dark : T.mid, borderBottom: active ? `2px solid ${T.blue}` : "2px solid transparent",
              }}
            >
              {t}
            </button>
          );
        })}
      </div>

      {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}

      {tab === "Overview" && (
        <div data-testid="run-detail-overview" style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {phases.map((p) => (
              <PhaseRow
                key={p.id}
                phase={p}
                canEdit={canEdit}
                advancing={advancing}
                onAdvance={handleAdvance}
                assignableUsers={assignableUsers}
                assigning={assigning}
                onAssignOwner={handleAssignPhaseOwner}
                currentUserId={currentUserId}
                currentRole={currentRole}
                decidingApproval={decidingApproval}
                onDecidePhaseApproval={handleDecidePhaseApproval}
                onResubmitPhaseApproval={handleResubmitPhaseApproval}
              />
            ))}
          </div>

          <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            {(lockedNeedingAttention.length > 0 || myOpenSubsteps.length > 0) && (
              <div data-testid="run-detail-need-attention" style={{ background: T.redSoft, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: T.fsXs, fontWeight: T.fwBold, color: T.red, marginBottom: 8 }}>
                  <AlertTriangle size={13} /> Needs attention
                </div>
                {lockedNeedingAttention.map((p) => (
                  <div key={p.id} style={{ fontSize: T.fsXs, color: T.dark, marginBottom: 4 }}>{p.name} — {p.lockReason}</div>
                ))}
                {myOpenSubsteps.map((s) => (
                  <div key={s.id} data-testid={`run-detail-need-attention-substep-${s.id}`} style={{ fontSize: T.fsXs, color: T.dark, marginBottom: 4 }}>
                    Your task: {s.label}
                  </div>
                ))}
              </div>
            )}

            <div data-testid="run-detail-participants" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>
                <Users size={13} /> Participants
              </div>
              {(Array.isArray(run.participants) && run.participants.length > 0
                ? run.participants
                : [run.ownerName, run.requestorName].filter(Boolean)
              ).map((name, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: T.fsXs, color: T.dark, marginBottom: 4 }}>
                  <User size={12} color={T.mid} /> {typeof name === "string" ? name : name?.name}
                </div>
              ))}
              {(!run.participants || run.participants.length === 0) && !run.ownerName && !run.requestorName && (
                <div style={{ fontSize: T.fsXs, color: T.mid }}>No participants recorded.</div>
              )}
            </div>

            <ActivityFeed activity={run.activity || []} limit={5} />
          </div>
        </div>
      )}

      {tab === "Phases" && (
        <div data-testid="run-detail-phases" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {phases.map((p) => (
            <PhaseRow
              key={p.id}
              phase={p}
              canEdit={canEdit}
              advancing={advancing}
              onAdvance={handleAdvance}
              assignableUsers={assignableUsers}
              assigning={assigning}
              onAssignOwner={handleAssignPhaseOwner}
              showSubsteps
              currentUserId={currentUserId}
              currentRole={currentRole}
              completing={completing}
              onToggleSubstep={handleToggleSubstep}
              decidingApproval={decidingApproval}
              onDecidePhaseApproval={handleDecidePhaseApproval}
              onDecideSubstepApproval={handleDecideSubstepApproval}
              onResubmitPhaseApproval={handleResubmitPhaseApproval}
              onResubmitSubstepApproval={handleResubmitSubstepApproval}
            />
          ))}
        </div>
      )}

      {tab === "Activity" && <ActivityFeed activity={run.activity || []} />}

      {tab === "Tasks" && (
        <div data-testid="run-detail-tasks" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {phases.map((p) => (
            <TaskPhaseChecklist
              key={p.id}
              phase={p}
              canEdit={canEdit}
              currentUserId={currentUserId}
              assignableUsers={assignableUsers}
              assigning={assigning}
              completing={completing}
              onAssignSubstep={handleAssignSubstep}
              onToggleSubstep={handleToggleSubstep}
            />
          ))}
        </div>
      )}

      {(tab === "Details" || tab === "Files") && (
        <div data-testid={`run-detail-${tab.toLowerCase()}-stub`} style={{ padding: 24, textAlign: "center", fontSize: T.fsSm, color: T.mid, background: T.card, border: `1px solid ${T.border}`, borderRadius: 11 }}>
          {tab} view is coming in a future update.
        </div>
      )}
    </div>
  );
}

// ─── WORKFLOWS-SLICE-3: person picker ────────────────────────────────────────
// Small inline <select> for reassigning a phase owner or sub-step assignee.
// Only rendered for canEdit (admin/editor) users.

function PersonPicker({ id, value, valueName, users, disabled, onChange, testId }) {
  return (
    <select
      data-testid={testId}
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(id, e.target.value)}
      style={{
        fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.dark, background: T.white,
        border: `1px solid ${T.border}`, borderRadius: 6, padding: "3px 6px", cursor: disabled ? "default" : "pointer",
        maxWidth: 160,
      }}
    >
      <option value="">{valueName || "Unassigned"}</option>
      {users.map((u) => (
        <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
      ))}
    </select>
  );
}

function TaskPhaseChecklist({ phase, canEdit, currentUserId, assignableUsers, assigning, completing, onAssignSubstep, onToggleSubstep }) {
  const substeps = phase.substeps || [];
  const isCurrent = phase.status === "current";

  return (
    <div
      data-testid={`run-detail-tasks-phase-${phase.id}`}
      style={{
        background: isCurrent ? T.blueSoft : T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px",
        opacity: phase.status === "locked" ? 0.65 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark }}>{phase.name}</span>
        <span style={{ fontSize: T.fsXxs, color: T.mid, textTransform: "uppercase", letterSpacing: 0.3 }}>{phase.status}</span>
      </div>

      {substeps.length === 0 ? (
        <div style={{ fontSize: T.fsXs, color: T.mid }}>No tasks in this phase.</div>
      ) : (
        substeps.map((s) => {
          const isAssignee = currentUserId && s.assigneeUserId === currentUserId;
          const canComplete = (isAssignee || canEdit) && isCurrent;
          const isDone = s.status === "done";
          return (
            <div
              key={s.id}
              data-testid={`run-detail-task-${s.id}`}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderTop: `1px solid ${T.border}` }}
            >
              <input
                type="checkbox"
                data-testid={`run-detail-task-checkbox-${s.id}`}
                checked={isDone}
                disabled={!canComplete || completing === s.id}
                onChange={(e) => onToggleSubstep(s.id, e.target.checked)}
                style={{ width: 15, height: 15, cursor: canComplete ? "pointer" : "not-allowed" }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: T.fsSm, color: isDone ? T.mid : T.dark, textDecoration: isDone ? "line-through" : "none" }}>
                  {s.label}
                  {s.required && <span style={{ color: T.red, marginLeft: 4 }}>*</span>}
                  {s.approvalGated && <span style={{ color: T.purple, marginLeft: 6, fontSize: T.fsXxs }}>(approval)</span>}
                </span>
                {s.dueDate && <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 1 }}>Due {fmtDate(s.dueDate)}</div>}
              </div>
              {canEdit ? (
                <PersonPicker
                  id={s.id}
                  value={s.assigneeUserId}
                  valueName={s.assigneeUserName || s.assigneeUserEmail}
                  users={assignableUsers}
                  disabled={assigning === s.id}
                  onChange={onAssignSubstep}
                  testId={`run-detail-task-assignee-${s.id}`}
                />
              ) : (
                <span style={{ fontSize: T.fsXxs, color: T.mid, whiteSpace: "nowrap" }}>
                  {s.assigneeUserName || s.assigneeUserEmail || "Unassigned"}
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

function ApprovalBadge({ decision }) {
  if (decision === "approved") {
    return <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.green, background: T.greenSoft, borderRadius: 5, padding: "2px 6px" }}>Approved</span>;
  }
  if (decision === "rejected") {
    return <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.red, background: T.redSoft, borderRadius: 5, padding: "2px 6px" }}>Rejected</span>;
  }
  return <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.purple, background: T.purpleBg, borderRadius: 5, padding: "2px 6px" }}>Pending approval</span>;
}

function ApprovalActions({ approval, canDecide, deciding, onDecide, onResubmit, testIdPrefix }) {
  if (!approval) return <ApprovalBadge decision={approval?.decision} />;
  const busy = deciding === (approval.runPhaseId ?? approval.runSubstepId);

  if (approval.decision === "rejected") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <ApprovalBadge decision="rejected" />
        {approval.note && <span style={{ fontSize: T.fsXxs, color: T.mid }}>“{approval.note}”</span>}
        {canDecide && onResubmit && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-resubmit`}
            onClick={onResubmit}
            disabled={busy}
            style={{
              background: T.white, color: T.blue, border: `1px solid ${T.blue}`, borderRadius: 6, padding: "3px 9px",
              fontSize: T.fsXxs, fontWeight: T.fwBold, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1,
            }}
          >
            Resubmit for approval
          </button>
        )}
      </span>
    );
  }

  if (approval.decision !== "pending") return <ApprovalBadge decision={approval.decision} />;
  if (!canDecide) return <ApprovalBadge decision="pending" />;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <ApprovalBadge decision="pending" />
      <button
        type="button"
        data-testid={`${testIdPrefix}-approve`}
        onClick={() => onDecide("approved")}
        disabled={busy}
        style={{
          background: T.green, color: T.white, border: "none", borderRadius: 6, padding: "3px 9px",
          fontSize: T.fsXxs, fontWeight: T.fwBold, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1,
        }}
      >
        Approve
      </button>
      <button
        type="button"
        data-testid={`${testIdPrefix}-reject`}
        onClick={() => {
          const note = window.prompt("Reason for rejecting (required):", "");
          if (note === null) return;
          if (!note.trim()) {
            window.alert("A reason is required to reject.");
            return;
          }
          onDecide("rejected", note.trim());
        }}
        disabled={busy}
        style={{
          background: T.white, color: T.red, border: `1px solid ${T.red}`, borderRadius: 6, padding: "3px 9px",
          fontSize: T.fsXxs, fontWeight: T.fwBold, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1,
        }}
      >
        Reject
      </button>
    </span>
  );
}

function PhaseRow({
  phase, canEdit, advancing, onAdvance, showSubsteps, assignableUsers = [], assigning, onAssignOwner,
  currentUserId = null, currentRole = "viewer", completing = null, onToggleSubstep = null,
  decidingApproval = null, onDecidePhaseApproval = null, onDecideSubstepApproval = null,
  onResubmitPhaseApproval = null, onResubmitSubstepApproval = null,
}) {
  const isDone = phase.status === "done";
  const isCurrent = phase.status === "current";
  const isAvailable = phase.status === "available";
  const isLocked = phase.status === "locked";
  const Icon = isDone ? CheckCircle2 : isLocked ? Lock : Circle;
  const iconColor = isDone ? T.green : isCurrent ? T.blue : isAvailable ? T.mid : T.mid;
  const ownerDisplayName = phase.ownerUserName || phase.ownerUserEmail || phase.ownerName;
  const latestPhaseApproval = (phase.approvals || [])[phase.approvals?.length - 1];
  // Approval decisions are intentionally NOT delegable to a generic editor —
  // only the resolved approver or an admin (server-enforced in
  // canActOnApproval) may approve/reject/resubmit.
  const canDecidePhaseApproval =
    Boolean(latestPhaseApproval) &&
    (currentRole === "admin" || (currentUserId && latestPhaseApproval.approverUserId === currentUserId));
  const phaseBlockedByApproval = phase.approvalGated && latestPhaseApproval && latestPhaseApproval.decision !== "approved";

  return (
    <div
      data-testid={`run-detail-phase-${phase.id}`}
      style={{
        background: isCurrent ? T.blueSoft : T.card,
        border: `1px solid ${T.border}`,
        borderLeft: `4px solid ${iconColor}`,
        borderRadius: 9,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Icon size={16} color={iconColor} />
          <div>
            <span style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark }}>{phase.name}</span>
            {!isDone && phase.slaState && SLA_META[phase.slaState] && (
              <span style={{ marginLeft: 8 }}>
                <SlaBadge state={phase.slaState} testId={`run-detail-phase-sla-${phase.id}`} />
              </span>
            )}
            {canEdit && onAssignOwner ? (
              <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: T.fsXs, color: T.mid }}>{phase.ownerRole}</span>
                <PersonPicker
                  id={phase.id}
                  value={phase.ownerUserId}
                  valueName={ownerDisplayName}
                  users={assignableUsers}
                  disabled={assigning === phase.id}
                  onChange={onAssignOwner}
                  testId={`run-detail-phase-owner-${phase.id}`}
                />
              </span>
            ) : (
              <span style={{ fontSize: T.fsXs, color: T.mid, marginLeft: 8 }}>
                {ownerDisplayName ? ownerDisplayName : phase.ownerRole}
                {ownerDisplayName && phase.ownerRole ? ` (${phase.ownerRole})` : ""}
              </span>
            )}
          </div>
        </div>
        {isCurrent && canEdit && (
          <button
            type="button"
            data-testid={`run-detail-advance-${phase.id}`}
            onClick={() => onAdvance(phase.id)}
            disabled={advancing || phaseBlockedByApproval}
            title={phaseBlockedByApproval ? "Waiting on approval" : undefined}
            style={{
              background: T.blue, color: T.white, border: "none", borderRadius: 7, padding: "6px 14px",
              fontSize: T.fsXs, fontWeight: T.fwBold,
              cursor: advancing || phaseBlockedByApproval ? "default" : "pointer",
              opacity: advancing || phaseBlockedByApproval ? 0.5 : 1,
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {advancing && <Loader2 size={12} />} Mark phase complete
          </button>
        )}
      </div>

      {isLocked && phase.lockReason && (
        <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 6, fontStyle: "italic" }}>Locked - {phase.lockReason}</div>
      )}
      {isAvailable && (
        <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 6, fontStyle: "italic" }}>Available - not yet started</div>
      )}
      {!isDone && phase.dueDate && (
        <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 6 }}>Due {fmtDate(phase.dueDate)}</div>
      )}
      {phase.approvalGated && (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: T.fsXxs, color: T.mid }}>Approver: {phase.approverRole}</span>
          {onDecidePhaseApproval && (
            <ApprovalActions
              approval={latestPhaseApproval}
              canDecide={canDecidePhaseApproval}
              deciding={decidingApproval}
              onDecide={(decision, note) => onDecidePhaseApproval(phase.id, decision, note)}
              onResubmit={onResubmitPhaseApproval ? () => onResubmitPhaseApproval(phase.id) : null}
              testIdPrefix={`run-detail-phase-approval-${phase.id}`}
            />
          )}
        </div>
      )}

      {showSubsteps && (phase.substeps || []).length > 0 && (
        <div style={{ marginTop: 10, paddingLeft: 8, borderLeft: `2px solid ${T.border}` }}>
          {phase.substeps.map((s) => {
            const isAssignee = currentUserId && s.assigneeUserId === currentUserId;
            const canComplete = Boolean(onToggleSubstep) && (isAssignee || canEdit) && isCurrent;
            const isSubstepDone = s.status === "done";
            return (
              <div
                key={s.id}
                data-testid={`run-detail-phase-task-${s.id}`}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}
              >
                {onToggleSubstep ? (
                  <input
                    type="checkbox"
                    data-testid={`run-detail-phase-task-checkbox-${s.id}`}
                    checked={isSubstepDone}
                    disabled={!canComplete || completing === s.id}
                    onChange={(e) => onToggleSubstep(s.id, e.target.checked)}
                    style={{ width: 13, height: 13, cursor: canComplete ? "pointer" : "not-allowed" }}
                  />
                ) : (
                  <Icon size={12} color={isSubstepDone ? T.green : T.mid} />
                )}
                <span style={{ flex: 1, fontSize: T.fsXs, color: isSubstepDone ? T.mid : T.dark, textDecoration: isSubstepDone ? "line-through" : "none" }}>
                  {s.label}
                </span>
                {s.approvalGated && onDecideSubstepApproval && (
                  <ApprovalActions
                    approval={(s.approvals || [])[s.approvals?.length - 1]}
                    canDecide={
                      currentRole === "admin" ||
                      (currentUserId && (s.approvals || [])[s.approvals?.length - 1]?.approverUserId === currentUserId)
                    }
                    deciding={decidingApproval}
                    onDecide={(decision, note) => onDecideSubstepApproval(s.id, decision, note)}
                    onResubmit={onResubmitSubstepApproval ? () => onResubmitSubstepApproval(s.id) : null}
                    testIdPrefix={`run-detail-substep-approval-${s.id}`}
                  />
                )}
                <span style={{ fontSize: T.fsXxs, color: T.mid, whiteSpace: "nowrap" }}>
                  {s.assigneeUserName || s.assigneeUserEmail || "Unassigned"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActivityFeed({ activity, limit }) {
  const items = limit ? activity.slice(0, limit) : activity;
  return (
    <div data-testid="run-detail-activity-feed" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>Recent activity</div>
      {items.length === 0 ? (
        <div style={{ fontSize: T.fsXs, color: T.mid }}>No activity yet.</div>
      ) : (
        items.map((a, i) => (
          <div key={a.id || i} style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 6 }}>
            {(a.at || a.text) && <span style={{ color: T.dark }}>{fmtDate(a.at)} — </span>}
            {a.text || a.label || String(a)}
            {a.actor && <span style={{ color: T.mid }}> · {a.actor}</span>}
          </div>
        ))
      )}
    </div>
  );
}
